import { useMsal } from '@azure/msal-react'
import { faCalendar, faShoppingCart } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Button,
  Group,
  Loader,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Stepper,
  Switch,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { DatePicker } from '@mantine/dates'
import { useMediaQuery } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSessionInfo } from '../../contexts/SessionContext'
import { addAdvertisementToCart } from '../../data/checkout.service'
import { DailyBusinessHours } from '../../models/DailyBusinessHours'
import { Device } from '../../models/Device'
import { FileWithUrl } from '../../models/FileWithUrl'
import {
  AdDesignPrice,
  AdSelectionTypes,
  ApprovalTypes,
  COLORS,
} from '../../utils/constants'
import {
  convertToEndOfDay,
  getBusinessDaysInfoFromDateRange,
  getDateStringFromRange,
  getDayName,
  setToMidnight,
  toLocalDateTimeString,
} from '../../utils/dateUtils'
import { formatNumber } from '../../utils/displayUtils'
import { QrSetupStep, QrConfig } from '../qr/QrSetupStep'
import { AdTypeSelector } from './AdTypeSelector'
import { AdUpload } from './AdUpload'
import PaidDesignConfiguration from './PaidDesignConfiguration'

const dateRangeOptions = [
  { value: '7', label: '1 Week' },
  { value: '14', label: '2 Weeks' },
  { value: '30', label: '1 Month' },
  { value: '90', label: '3 Months' },
  { value: '-1', label: 'Choose Dates' },
]

function AdConfiguration(props: AdConfigurationProps) {
  const { onCompletion, onCancel, device, businessHours } = props
  const [adFile, setAdFile] = useState<FileWithUrl | null>(null)
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ])
  const [designNotes, setDesignNotes] = useState('')
  // QR step is only added when the user enables it in Ad Details

  const [currentStep, setCurrentStep] = useState(0)
  const [adName, setAdName] = useState('')
  const [adSelectionType, setAdSelectionType] = useState<AdSelectionTypes>(
    AdSelectionTypes.UploadAd,
  )
  const [numberOfDaysToRunAd, setNumberOfDaysToRunAd] = useState<string>('30') // default to 1 Month
  const [autoRenew, setAutoRenew] = useState<boolean>(true)
  const [qrConfig, setQrConfig] = useState<QrConfig>({
    qrEnabled: false,
    destinationUrl: '',
    qrPositionXPercent: null,
    qrPositionYPercent: null,
    qrSizePercent: null,
  })
  const [qrHasInteracted, setQrHasInteracted] = useState(false)
  const [autoRenewMode, setAutoRenewMode] = useState<'once' | 'auto'>('auto')
  const lastStepIndex = qrConfig.qrEnabled ? 3 : 2
  const mobileStyles = {
    stepBody: {
      display: 'none',
    },
    separator: {
      marginLeft: -2,
      marginRight: -2,
      height: 7,
    },
  }
  const isMobile = useMediaQuery('(max-width: 768px)')

  const getClosedDays = (date: Date) => {
    const dayName = getDayName(date.getDay())
    return businessHours[dayName].closed
  }

  // Get the minimum date for the date picker
  // If business is closed today, start from tomorrow. Otherwise, start from today
  const getMinDate = () => {
    const today = new Date()
    const dayName = getDayName(today.getDay())
    const todayHours = businessHours[dayName]

    // If business is closed today, return tomorrow
    if (todayHours.closed) {
      const tomorrow = new Date(today)
      tomorrow.setDate(tomorrow.getDate() + 1)
      return tomorrow
    }

    // If business is open today, check if it's currently closed based on time
    const currentTime = today.getHours() * 60 + today.getMinutes()
    const closeTime = parseTimeString(todayHours.close)

    // If current time is outside business hours, return tomorrow
    if (currentTime >= closeTime) {
      const tomorrow = new Date(today)
      tomorrow.setDate(tomorrow.getDate() + 1)
      return tomorrow
    }

    // Business is currently open, so allow today
    return today
  }

  // Parse time string (e.g., "90AM", "5:0) to minutes since midnight
  const parseTimeString = (timeString: string): number => {
    const [time, period] = timeString.split(' ')
    const [hours, minutes] = time.split(':').map(Number)

    let hour24 = hours
    if (period === 'PM' && hours !== 12) {
      hour24 = hours + 12
    } else if (period === 'AM' && hours === 12) {
      hour24 = 0
    }

    return hour24 * 60 + (minutes || 0) // Handle cases where minutes might be undefined
  }

  const daysInfo = getBusinessDaysInfoFromDateRange(
    dateRange[0] && dateRange[1]
      ? [setToMidnight(dateRange[0]), convertToEndOfDay(dateRange[1])]
      : dateRange,
    businessHours,
    numberOfDaysToRunAd !== '-1' ? parseInt(numberOfDaysToRunAd) : null,
  )
  const [files, setFiles] = useState<FileWithUrl[]>([])
  const [isLoadingAddToCart, setIsLoadingAddToCart] = useState<boolean>(false)
  const { instance } = useMsal()
  const { session, setSession } = useSessionInfo()
  const navigate = useNavigate()

  // Calculate pricing based on selected time period
  const getSelectedPrice = () => {
    switch (numberOfDaysToRunAd) {
      case '7': // 1 week
        return device.weeklyPrice
      case '14': // 2 weeks
        return device.weeklyPrice * 2
      case '30': // 1 month
        return device.monthlyPrice
      case '90': // 3 months
        return device.monthlyPrice * 3
      default:
        return device.monthlyPrice
    }
  }

  const selectedPrice = getSelectedPrice()

  const handleAddToCart = async () => {
    if ((session?.allLocationsBundleCount ?? 0) > 0) {
      notifications.show({
        title: 'All Locations bundle in cart',
        message:
          'You already have an All Locations bundle in your cart. Remove it before booking individual locations, or proceed to checkout with your bundle.',
        color: 'yellow',
        autoClose: 6000,
      })
      return
    }

    setIsLoadingAddToCart(true)

    try {
      if (!session?.organizationId)
        throw new Error("Failed to load currently user's organization")

      let startDate = null
      let endDate = null

      if (numberOfDaysToRunAd === '-1') {
        if (!dateRange[0] || !dateRange[1]) {
          throw new Error('Start and end dates are required')
        }
        startDate = setToMidnight(dateRange[0])
        endDate = convertToEndOfDay(dateRange[1])
      }

      // Get user's timezone
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

      const newItem = await addAdvertisementToCart(
        {
          name: adName,
          adFiles: files,
          startDateLocal: startDate ? toLocalDateTimeString(startDate) : null,
          endDateLocal: endDate ? toLocalDateTimeString(endDate) : null,
          timezone: timezone,
          designNotes: designNotes,
          adSelectionType: adSelectionType,
          deviceId: device.id!,
          imageUrl:
            adSelectionType === AdSelectionTypes.UploadAd && adFile?.url != null
              ? adFile.url
              : '',
          organizationId: session.organizationId,
          numberOfDaysToRunAd:
            numberOfDaysToRunAd !== '-1' ? parseInt(numberOfDaysToRunAd) : null,
          autoRenew: autoRenew,
          qrEnabled: qrConfig.qrEnabled,
          destinationUrl: qrConfig.qrEnabled ? qrConfig.destinationUrl : null,
          qrPositionXPercent: qrConfig.qrEnabled
            ? qrConfig.qrPositionXPercent
            : null,
          qrPositionYPercent: qrConfig.qrEnabled
            ? qrConfig.qrPositionYPercent
            : null,
          qrSizePercent: qrConfig.qrEnabled ? qrConfig.qrSizePercent : null,
        },
        instance,
      )
      if (session) {
        setSession({
          ...session,
          itemsInCart: [...session.itemsInCart, newItem],
        })
      }
      notifications.show({
        id: 'addToCart',
        style: {
          maxWidth: 350,
          marginLeft: 'auto', // Push it to the right
          marginTop: '70px',
        },
        title: (
          <Title pt={10} size='h4'>
            Advertisement added to cart
          </Title>
        ),
        styles: {
          root: {
            borderLeft: 'none',
          },
          closeButton: {
            alignSelf: 'flex-start',
            marginTop: '5px',
          },
        },
        message: (
          <Stack>
            <Text>Advertisement was successfully added to cart.</Text>
            <Button
              size='md'
              mb={10}
              onClick={() => {
                navigate('/cart')
                notifications.hide('addToCart')
              }}
            >
              Go to Cart
            </Button>
          </Stack>
        ),
        color: 'none',
        position: 'top-right',
        autoClose: 5000,
      })
      onCompletion()
    } catch (error) {
      notifications.show({
        title: 'Error adding advertisement to cart',
        message:
          'Please try again. If you are unable to resolve the issue, please contact us for help',
        autoClose: false,
      })
    } finally {
      setIsLoadingAddToCart(false)
    }
  }

  const nextStep = async () => {
    if (currentStep === lastStepIndex) {
      await handleAddToCart()
    } else {
      setCurrentStep((current) => current + 1)
    }
  }
  const prevStep = () => {
    if (currentStep === 0) {
      onCancel()
    } else {
      setCurrentStep((current) => current - 1)
    }
  }

  const isNextButtonEnabled = (stepNumber: number) => {
    if (stepNumber === 0) {
      if (numberOfDaysToRunAd === '-1') {
        return (
          dateRange[0] != null && dateRange[1] != null && daysInfo.openDays > 0
        )
      }
      return true
    }
    if (stepNumber === 1) {
      return adSelectionType != null
    }
    if (stepNumber === 2) {
      if (adSelectionType === AdSelectionTypes.UploadAd) {
        return adFile != null
      } else if (adSelectionType === AdSelectionTypes.PayForDesign) {
        return files.length > 0 && designNotes !== ''
      }
      return false
    }
    if (stepNumber === 3) {
      if (!qrHasInteracted) return false
      if (!qrConfig.destinationUrl) return false
      try {
        const u = new URL(qrConfig.destinationUrl)
        if (u.protocol !== 'http:' && u.protocol !== 'https:') return false
      } catch {
        return false
      }
      if (
        qrConfig.qrPositionXPercent == null ||
        qrConfig.qrPositionYPercent == null ||
        qrConfig.qrSizePercent == null
      ) {
        return false
      }
      return true
    }
    return true
  }

  const dateRangeButtons = dateRangeOptions.map((option) => {
    let optionPrice = 0
    switch (option.value) {
      case '7':
        optionPrice = device.weeklyPrice
        break
      case '14':
        optionPrice = device.weeklyPrice * 2
        break
      case '30':
        optionPrice = device.monthlyPrice
        break
      case '90':
        optionPrice = device.monthlyPrice * 3
        break
      default:
        optionPrice = device.monthlyPrice
    }
    return (
      <Button
        key={option.value}
        variant={numberOfDaysToRunAd === option.value ? 'filled' : 'outline'}
        color={numberOfDaysToRunAd === option.value ? COLORS.LightGreen : 'gray'}
        onClick={() => {
          setNumberOfDaysToRunAd(option.value)
          if (option.value === '-1') {
            setAutoRenew(false)
            setAutoRenewMode('once')
          }
        }}
        radius='md'
        size='md'
        fullWidth={isMobile}
        px={isMobile ? 6 : undefined}
        style={{ minHeight: 80, minWidth: isMobile ? 0 : 100 }}
      >
        <Stack gap={4} align='center'>
          <Group gap={6} justify='center' wrap='nowrap'>
            {option.value === '-1' && <FontAwesomeIcon icon={faCalendar} />}
            <Text fz={isMobile ? 'xs' : 'sm'} style={{ whiteSpace: 'nowrap' }}>
              {option.label}
            </Text>
          </Group>
          {option.value !== '-1' && optionPrice > 0 && (
            <Text
              size='sm'
              fz={isMobile ? 13 : 16}
              fw={700}
              c={numberOfDaysToRunAd === option.value ? 'white' : COLORS.LightGreen}
            >
              ${formatNumber(optionPrice)}
            </Text>
          )}
        </Stack>
      </Button>
    )
  })

  return (
    <Stack>
      <Stepper
        active={currentStep}
        onStepClick={setCurrentStep}
        styles={{
          content: { paddingTop: 10 },
          ...(isMobile ? mobileStyles : {}),
        }}
        size='sm'
      >
        <Stepper.Step
          label='Ad Run Length'
          description={
            numberOfDaysToRunAd === '-1'
              ? getDateStringFromRange(dateRange, null)
              : dateRangeOptions.find(
                  (option) => option.value === numberOfDaysToRunAd,
                )?.label
          }
          style={{ justifyItems: 'center' }}
        >
          {isMobile ? (
            <SimpleGrid cols={3} spacing='xs' my={10}>
              {dateRangeButtons}
            </SimpleGrid>
          ) : (
            <Group
              gap='sm'
              my={20}
              pt={10}
              w='100%'
              wrap='wrap'
              grow
              justify='center'
            >
              {dateRangeButtons}
            </Group>
          )}

          {numberOfDaysToRunAd === '-1' && (
            <DatePicker
              pt={20}
              minDate={getMinDate()}
              display={'flex'}
              excludeDate={getClosedDays}
              style={{ justifyContent: 'center' }}
              type='range'
              value={dateRange}
              onChange={setDateRange}
              maxLevel='year'
              size={isMobile ? 'md' : 'lg'}
              weekendDays={[]}
              allowSingleDateInRange
            />
          )}
          {numberOfDaysToRunAd !== '-1' && (
            <Stack gap={isMobile ? 8 : 2} pt={isMobile ? 16 : 10}>
              <SegmentedControl
                value={autoRenewMode}
                onChange={(value) => {
                  const v = value as 'once' | 'auto'
                  setAutoRenewMode(v)
                  setAutoRenew(v === 'auto')
                }}
                data={[
                  { label: 'Run Once', value: 'once' },
                  { label: 'Auto-Renew', value: 'auto' },
                ]}
                color={COLORS.LightGreen}
                size={isMobile ? 'md' : 'sm'}
                fullWidth={isMobile}
                mt={isMobile ? 8 : 0}
              />
              {autoRenewMode === 'auto' && (
                <Text size='sm' c='dimmed' pt={2}>
                  *The card on file will be charged each time the ad renews, 1
                  day before ending.
                </Text>
              )}
            </Stack>
          )}

        </Stepper.Step>

        <Stepper.Step
          label='Design Type'
          description={adSelectionType}
          allowStepSelect={isNextButtonEnabled(0)}
        >
          <AdTypeSelector
            setAdSelectionType={(type) => {
              setAdSelectionType(type)
              if (type === AdSelectionTypes.PayForDesign) {
                setQrConfig((prev) => ({ ...prev, qrEnabled: false }))
              }
            }}
            adSelectionType={adSelectionType}
            requiresApproval={device.approvalType === ApprovalTypes.Manual}
            isMobile={isMobile}
          />
        </Stepper.Step>

        <Stepper.Step
          label='Ad Details'
          description='Please add details'
          allowStepSelect={isNextButtonEnabled(0)}
        >
          <Stack>
            <TextInput
              radius='md'
              label='Ad Name'
              placeholder='Summer Blowout Sale'
              value={adName}
              styles={{ input: { fontSize: '16px' } }}
              onChange={(e) => setAdName(e.target.value)}
            />
            {adSelectionType === AdSelectionTypes.UploadAd && (
              <AdUpload
                onFileUploadComplete={setAdFile}
                adFile={adFile}
                showCanvaText={true}
              />
            )}
            {adSelectionType === AdSelectionTypes.PayForDesign && (
              <PaidDesignConfiguration
                setFiles={setFiles}
                designNotes={designNotes}
                setDesignNotes={setDesignNotes}
              />
            )}
            {adSelectionType === AdSelectionTypes.UploadAd &&
              (isMobile ? (
                <Switch
                  checked={qrConfig.qrEnabled}
                  onChange={(event) => {
                    const enabling = event.currentTarget.checked
                    if (enabling) setQrHasInteracted(false)
                    setQrConfig({ ...qrConfig, qrEnabled: enabling })
                  }}
                  label='QR Scan Tracking'
                  color={COLORS.LightGreen}
                  size='lg'
                  mt={4}
                />
              ) : (
                <SegmentedControl
                  value={qrConfig.qrEnabled ? 'enabled' : 'disabled'}
                  onChange={(value) => {
                    const enabling = value === 'enabled'
                    if (enabling) setQrHasInteracted(false)
                    setQrConfig({ ...qrConfig, qrEnabled: enabling })
                  }}
                  data={[
                    { label: 'QR Scan Tracking Disabled', value: 'disabled' },
                    { label: 'QR Scan Tracking Enabled', value: 'enabled' },
                  ]}
                  color={COLORS.LightGreen}
                  size='sm'
                  fullWidth
                />
              ))}
          </Stack>
        </Stepper.Step>

        {qrConfig.qrEnabled && (
          <Stepper.Step
            label='QR Code'
            description='Position & URL'
            allowStepSelect={isNextButtonEnabled(0) && isNextButtonEnabled(1) && isNextButtonEnabled(2)}
          >
            <QrSetupStep
              config={qrConfig}
              onChange={setQrConfig}
              baseImageUrl={adFile?.url ?? null}
              showToggle={false}
              onInteracted={() => setQrHasInteracted(true)}
            />
          </Stepper.Step>
        )}
      </Stepper>

      <Stack
        gap={0}
        pos='sticky'
        bg='white'
        pt={10}
        style={{
          bottom: 0,
          borderTop: '1px solid lightgray',
        }}
      >
        <Stack gap={0} align='flex-end'>
          {numberOfDaysToRunAd === '-1' && (
            <>
              <Text>
                {`${daysInfo.openDays} ${
                  daysInfo.openDays === 1 ? 'Business Day' : 'Business Days'
                } @ $${formatNumber(device.dailyPrice)}/day`}
              </Text>
            </>
          )}
          {adSelectionType != null &&
            adSelectionType === AdSelectionTypes.PayForDesign && (
              <Text>+ ${AdDesignPrice} Design Fee</Text>
            )}

          <Title size='h3' style={{ alignSelf: 'flex-end' }}>
            Total: $
            {formatNumber(
              (numberOfDaysToRunAd === '-1'
                ? daysInfo.openDays * device.dailyPrice
                : selectedPrice) +
                (adSelectionType != null &&
                adSelectionType === AdSelectionTypes.PayForDesign
                  ? AdDesignPrice
                  : 0),
            )}
          </Title>
        </Stack>
        <Group justify='space-between' mt='sm' mb='sm'>
          <Button
            variant='default'
            onClick={prevStep}
            radius='md'
            disabled={isLoadingAddToCart}
          >
            {currentStep === 0 ? 'Cancel' : 'Back'}
          </Button>
          <Button
            disabled={!isNextButtonEnabled(currentStep) || isLoadingAddToCart}
            onClick={nextStep}
            radius='md'
          >
            {currentStep === lastStepIndex ? (
              <Group>
                {isLoadingAddToCart ? (
                  <Loader size='xs' />
                ) : (
                  <FontAwesomeIcon icon={faShoppingCart} />
                )}
                <Text>Add To Cart</Text>
              </Group>
            ) : (
              <Text>Next</Text>
            )}
          </Button>
        </Group>
      </Stack>
    </Stack>
  )
}

interface AdConfigurationProps {
  onCompletion: () => void
  onCancel: () => void
  device: Device
  businessHours: Record<string, DailyBusinessHours>
}

export default AdConfiguration
